/**
 * <MoneyAmountField /> — opinionated wrapper around FormField.Currency
 * that adds the three things every money input in the app needs:
 *
 *   1. **Live FX preview** — when the field's currency differs from
 *      the user's primary, a sub-label shows "≈ $X.XX USD" using the
 *      shared ``useFxConvert`` hook.
 *   2. **Balance validation** — optionally checks the typed amount
 *      against an account's available balance and surfaces an inline
 *      warning ("Excede el saldo de la cuenta") without blocking the
 *      submit (the parent form decides whether to enforce it).
 *   3. **Consistent styling** — same height/font/badge across every
 *      form, so TransferDialog, MovementEditDialog, QuickAdd, and the
 *      onboarding wizard all feel identical.
 *
 * Drop-in usage:
 *
 *   <MoneyAmountField
 *     name="amount"
 *     label="Monto"
 *     required
 *     currency="ARS"
 *     primaryCurrency="USD"           // for the FX preview
 *     balance={5000}                  // optional inline warning
 *     value={amount}
 *     onChange={(v) => setAmount(v)}
 *     onBlur={() => markTouched("amount")}
 *     error={touched.amount ? errors.amount : undefined}
 *   />
 *
 * The component is intentionally PRESENTATION-only; the parent owns
 * the value and the validation. It just renders the right hints next
 * to whatever the user typed.
 */
import { useMemo } from "react";
import { FormField } from "./form-ux";
import { useFxConvert } from "../hooks/queries";
import { formatMoney } from "../lib/api";


export function MoneyAmountField({
  name,
  label,
  required = false,
  hint,
  error,                              // optional inline error (rose)
  currency,                           // the amount's currency, e.g. "ARS"
  primaryCurrency,                    // the user's primary, e.g. "USD"
  balance,                            // optional reference balance for warnings
  balanceWarningOnly = true,          // warning vs hard error when over balance
  value,                              // controlled value (string OK)
  onChange,                           // (newValue: string) => void
  onBlur,
  placeholder = "0.00",
  className = "",
  testid,
  inputProps = {},                    // pass-through to FormField.Currency
}) {
  const fxConvert = useFxConvert();
  const isEs = typeof navigator !== "undefined" && navigator.language?.startsWith("es");

  const numeric = useMemo(() => {
    const n = parseFloat(String(value).replace(/,/g, "."));
    return Number.isFinite(n) ? n : null;
  }, [value]);

  // ── FX preview ────────────────────────────────────────────────────
  const showFx = !!(
    primaryCurrency &&
    currency &&
    String(primaryCurrency).toUpperCase() !== String(currency).toUpperCase()
  );
  const converted = useMemo(() => {
    if (!showFx || numeric == null) return null;
    return fxConvert(numeric, currency, primaryCurrency);
  }, [showFx, numeric, currency, primaryCurrency, fxConvert]);

  // ── Balance warning ───────────────────────────────────────────────
  const overBalance = useMemo(() => {
    if (balance == null) return false;
    if (numeric == null) return false;
    return numeric > Number(balance);
  }, [balance, numeric]);

  const balanceWarning = overBalance
    ? (isEs
        ? `Excede el saldo disponible (${formatMoney(balance, currency)}).`
        : `Exceeds the available balance (${formatMoney(balance, currency)}).`)
    : null;

  // If the parent passed an explicit error it wins; otherwise we may
  // surface the over-balance message as either an error (hard) or
  // append it to the hint (soft).
  const effectiveError = error
    || (overBalance && !balanceWarningOnly ? balanceWarning : undefined);

  // Hint composition: FX preview ALWAYS wins over generic hint when
  // both are present, so the user sees "≈ $X USD" first; balance
  // warning (when soft) goes second.
  const composedHint = useMemo(() => {
    const parts = [];
    if (showFx && converted != null) {
      parts.push(`≈ ${formatMoney(converted, primaryCurrency)}`);
    }
    if (balance != null && !overBalance) {
      parts.push(
        isEs
          ? `Saldo: ${formatMoney(balance, currency)}`
          : `Balance: ${formatMoney(balance, currency)}`,
      );
    }
    if (overBalance && balanceWarningOnly && !error) {
      parts.push(`⚠️ ${balanceWarning}`);
    }
    if (hint) parts.push(hint);
    return parts.length ? parts.join(" · ") : undefined;
  }, [showFx, converted, balance, overBalance, balanceWarningOnly, balanceWarning, hint, isEs, currency, primaryCurrency, error]);

  return (
    <FormField
      label={label}
      name={name}
      required={required}
      hint={composedHint}
      error={effectiveError}
      className={className}
      testid={testid}
    >
      <FormField.Currency
        currency={currency}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange?.(e?.target ? e.target.value : e)}
        onBlur={onBlur}
        {...inputProps}
      />
    </FormField>
  );
}


export default MoneyAmountField;
